import React, { useState, useEffect } from "react";
import GridContainer from "../../components/Grid/GridContainer";
import GridItem from "../../components/Grid/GridItem";
import axios from "store/axios/axios-pr";
import { handleError } from "../../shared/utility";
import PaymentReportsList from "./PaymentReportsList";
import CustomPDFViewer from "../../components/CustomPDFViewer/CustomPDFViewer";
import { renderGeneratedPaymentReportLegacyHTML } from "./generatedPaymentReportLegacyHTML";

const PaymentReports = () => {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);
    const [paymentIdFilter, setPaymentIdFilter] = useState("");
    const [showVoided, setShowVoided] = useState(false);
    const [openedPdfView, setOpenedPdfView] = useState(false);
    const [localPdfData, setLocalPdfData] = useState(null);
    const [regeneratingId, setRegeneratingId] = useState(null);

    const createReportPreview = async (htmlContent) => {
        try {
            const response = await axios.post("/pdf", { htmlContent });
            return response.data;
        } catch (e) {
            handleError(e);
            alert('Could not load PDF, please try again or contact tech support.');
            return null;
        }
    };

    const loadEntries = async (options = {}) => {
        const nextPage = options.page !== undefined ? options.page : page;
        const nextPageSize = options.pageSize !== undefined ? options.pageSize : pageSize;
        const nextPaymentId = options.paymentId !== undefined ? options.paymentId : paymentIdFilter;
        const nextShowVoided = options.showVoided !== undefined ? options.showVoided : showVoided;
        try {
            setLoading(true);
            const params = [
                `page=${nextPage}`,
                `pageSize=${nextPageSize}`,
                `include_voided=${nextShowVoided ? 1 : 0}`
            ];
            if (nextPaymentId) params.push(`payment_id=${encodeURIComponent(nextPaymentId)}`);
            const response = await axios.get(`/payment_reports2?${params.join("&")}`);
            setEntries(response.data.data || []);
            setTotal(response.data.total || 0);
            setPage(response.data.page || 0);
            setPageSize(response.data.pageSize || nextPageSize);
        } catch (e) {
            handleError(e);
            alert('Error fetching the Payment Reports');
        } finally {
            setLoading(false);
        }
    };

    const openEntry = async (entry) => {
        const data = entry && entry.data ? entry.data : {};
        const htmlContent = data.html_content;
        if (!htmlContent) {
            alert("This payment report history row does not have saved HTML.");
            return;
        }
        const pdf = await createReportPreview(htmlContent);
        if (pdf) {
            setLocalPdfData(pdf);
            setOpenedPdfView(true);
        }
    };

    const regenerateEntry = async (entry) => {
        const paymentId = entry && entry.payment_id;
        if (!paymentId) {
            alert("This payment report history row does not have a Payment ID.");
            return;
        }

        try {
            setRegeneratingId(entry.id);
            const reportResponse = await axios.get(`/payment_report_preview?payment_id=${paymentId}`);
            const htmlContent = renderGeneratedPaymentReportLegacyHTML(reportResponse.data);
            await axios.post('/payment_reports2', {
                payment_id: paymentId,
                html_content: htmlContent,
                data: {
                    report: reportResponse.data,
                    payment_id: paymentId,
                    invoice_id: reportResponse.data.invoice_id,
                    rs_id: reportResponse.data.rs_id
                }
            });
            await loadEntries({ page: 0 });
            const pdf = await createReportPreview(htmlContent);
            if (pdf) {
                setLocalPdfData(pdf);
                setOpenedPdfView(true);
            }
        } catch (e) {
            handleError(e);
            alert('Could not regenerate the Payment Report.');
        } finally {
            setRegeneratingId(null);
        }
    };

    useEffect(() => {
        loadEntries({ page: 0 });
    }, []);

    return (
        <>
            <GridContainer>
                <GridItem xs={12} sm={12} md={12}>
                    <PaymentReportsList
                        data={entries}
                        total={total}
                        page={page}
                        pageSize={pageSize}
                        loading={loading}
                        paymentIdFilter={paymentIdFilter}
                        setPaymentIdFilter={setPaymentIdFilter}
                        showVoided={showVoided}
                        setShowVoided={setShowVoided}
                        loadEntries={loadEntries}
                        openEntry={openEntry}
                        regenerateEntry={regenerateEntry}
                        regeneratingId={regeneratingId}
                    />
                </GridItem>
            </GridContainer>

            {localPdfData && openedPdfView && (
                <CustomPDFViewer
                    pdfData={localPdfData}
                    open={true}
                    close={() => {
                        setLocalPdfData(null);
                        setOpenedPdfView(false);
                    }}
                />
            )}
        </>
    );
};

export default PaymentReports;
